import React, { useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Container,
  Drawer,
  MenuItem,
  Box,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  boxShadow: theme.shadows[1],
  padding: '8px 12px',
}));

const navItems = [
  { label: 'About Us', path: '/about-us' },
  { label: 'Locations', path: '/locations' },
  { label: 'Programs', path: '/programs' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Bookings', path: '/bookings' },
  { label: 'Policies', path: '/policies' },
  { label: 'Contact Us', path: '/contact-us' },
];

function NavBar() {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const location = useLocation();

  const toggleDrawer = (newOpen) => () => setOpen(newOpen);

  const isActive = (path) => location.pathname === path;

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          {/* Logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <Link to="/">
              <img
                src="/images/SJ_SWIMMING_White_Red.png"
                alt="SJ Swimming"
                style={{ height: '40px', width: 'auto' }}
              />
            </Link>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                sx={{
                  color: isActive(item.path)
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  fontWeight: isActive(item.path) ? 700 : 400,
                  borderBottom: isActive(item.path)
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  borderRadius: 0,
                  px: 1.5,
                  py: 1,
                  transition: 'color 0.2s, border-color 0.2s',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton aria-label="Open menu" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: { top: 'var(--template-frame-height, 0px)' },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton aria-label="Close menu" onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                {navItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    component={Link}
                    to={item.path}
                    onClick={toggleDrawer(false)}
                    sx={{
                      color: isActive(item.path)
                        ? theme.palette.primary.main
                        : 'text.primary',
                      fontWeight: isActive(item.path) ? 700 : 400,
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}

export default NavBar;
